import { useRef, useState } from 'react'
import {
  Button,
  FormScroll,
  Icon,
  Modal,
  Pill,
  PillCluster,
  Tabs,
  TopBar,
  toastError,
  toastSuccess,
} from 'admination-react'
import type { Me as AuthSession } from '@/core/api/auth'
import { assertWriteIdentity, captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import type { PendingErstgespraech } from '@/core/state/appStore'
import { me, setActiveOrganisation, setActiveVariant } from '@/core/data/session'
import { updateOrganisation } from '@/core/data/organisation'
import { organisationGruenden } from '@/core/api/organisation'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { initialFormValues, type FormValues } from '@/core/components/form/formModel'
import { descriptionFor } from '@/variants'
import { gateAdvance } from '@/core/lib/advanceGate'
import { setAt } from '@/core/lib/path'
import { todayIso } from '@/core/lib/dates'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { AccountSignOut } from '@/core/components/UserMenu'
import { MeineEinstellungen } from '@/core/components/einstellungen/MeineEinstellungen'
import { CreateOrganisation, emptyOrganisationDraft } from './CreateOrganisation'
import { Login } from './Login'

const conversation = descriptionFor('azav').firstConversation
if (!conversation) throw new Error("Das Erstgespräch ist nicht verfügbar.")
const { form: erstgespraechForm, check: checkErstgespraech, manualFields: erstgespraechManualFields, advance: advanceNewErstgespraech } = conversation

type View = 'board' | 'erstgespraech' | 'organisation' | 'login'

export function OrganisationSetup({ session, onCreated }: {
  session: AuthSession
  onCreated: (draft?: PendingErstgespraech) => Promise<void>
}) {
  const [view, setView] = useState<View>('board')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [profileName, setProfileName] = useState({ vorname: session.vorname ?? '', nachname: session.nachname ?? '' })
  const currentProfileName = useRef(profileName)
  currentProfileName.current = profileName
  const [started, setStarted] = useState(false)
  const [organisation, setOrganisation] = useState(emptyOrganisationDraft)
  const [values, setValues] = useState<FormValues>(() => initialFormValues(erstgespraechForm, {
    Maßnahme: { 'Datum des Gesprächs': todayIso() },
    Gesprächsführer: { 'Name, Vorname': [session.nachname, session.vorname].filter(Boolean).join(', ') },
  }))
  const [attempted, setAttempted] = useState(false)
  const [busy, setBusy] = useState(false)
  const working = useRef(false)
  const organisationId = useRef<string | null>(null)
  const pending = useRef<PendingErstgespraech | null>(null)
  const advanceAfterCreation = useRef(false)
  const advanced = useRef(false)
  const check = checkErstgespraech(values)
  const account = [profileName.vorname, profileName.nachname].filter(Boolean).join(' ') || session.email || "Konto"
  function closeSettings() {
    ;(document.activeElement as HTMLElement | null)?.blur()
    setSettingsOpen(false)
  }
  function changeProfileName(name: typeof profileName) {
    const previous = currentProfileName.current
    currentProfileName.current = name
    setProfileName(name)
    setValues((current) => {
      if (current.Gesprächsführer?.['Name, Vorname'] !== [previous.nachname, previous.vorname].filter(Boolean).join(', ')) return current
      const next = structuredClone(current)
      setAt(next, 'Gesprächsführer.Name, Vorname', [name.nachname, name.vorname].filter(Boolean).join(', '))
      return next
    })
  }

  function draft(): PendingErstgespraech {
    pending.current = { values, coachingId: pending.current?.coachingId ?? crypto.randomUUID(), teilnehmendeId: pending.current?.teilnehmendeId ?? crypto.randomUUID() }
    return pending.current
  }
  function openOrganisation(advance: boolean) {
    advanceAfterCreation.current = advance
    if (started) draft()
    setView('organisation')
  }
  async function create() {
    if (working.current) return
    let operationIdentity = captureWriteIdentity()
    working.current = true
    setBusy(true)
    try {
      const existingOrganisation = organisationId.current
      if (!organisationId.current) {
        const { name, ...daten } = organisation.data
        const created = await organisationGruenden(name.trim(), organisation.variants, daten)
        assertWriteIdentity(operationIdentity)
        organisationId.current = created.id
      }
      operationIdentity = await setActiveOrganisation(organisationId.current)
      assertWriteIdentity(operationIdentity)
      if (started) {
        const identity = await me()
        operationIdentity = { ...operationIdentity, scope: JSON.stringify([session.user_id, organisationId.current, identity.variant]) }
        assertWriteIdentity(operationIdentity)
        if (!identity.variantsOffered.includes('azav')) throw new Error("Für das Erstgespräch muss die Organisation AVGS anbieten.")
        if (identity.variant !== 'azav') {
          operationIdentity = await setActiveVariant(organisationId.current, 'azav')
          assertWriteIdentity(operationIdentity)
          await me()
          operationIdentity = { ...operationIdentity, scope: JSON.stringify([session.user_id, organisationId.current, 'azav']) }
          assertWriteIdentity(operationIdentity)
        }
      }
      if (existingOrganisation) await updateOrganisation(organisationId.current, organisation.data)
      assertWriteIdentity(operationIdentity)
      if (advanceAfterCreation.current && !advanced.current) {
        const link = await advanceNewErstgespraech(draft())
        assertWriteIdentity(operationIdentity)
        advanced.current = true
        toastSuccess(`Stammdaten-Link an ${link.sent_to} gesendet.`)
      }
      const completion = onCreated(started && !advanced.current ? draft() : undefined)
      operationIdentity = captureWriteIdentity()
      await completion
    } catch (error) {
      if (!isWriteIdentityCurrent(operationIdentity)) return
      toastError(friendlyErrorMessage(error, "Die Organisation oder das Erstgespräch konnte nicht gespeichert werden. Ihre Eingaben bleiben erhalten. Bitte versuchen Sie es erneut."))
    } finally { working.current = false; if (isWriteIdentityCurrent(operationIdentity)) setBusy(false) }
  }

  if (view === 'login') return <Login activeAccount={{ email: session.email ?? '', onContinue: () => setView('board') }} />
  return <div className="page" id="page"><div className="layout" id="base-layout" data-main-layout>
    {view === 'organisation' ? <CreateOrganisation value={organisation} onChange={setOrganisation} variants={session.variants}
      busy={busy} variantsLocked={organisationId.current !== null} requiresAzav={started} onBack={() => setView(started ? 'erstgespraech' : 'board')} onSubmit={() => void create()} /> : <>
      <TopBar className="bar--responsive" back={{ label: "Zurück", onClick: () => setView(view === 'erstgespraech' ? 'board' : 'login') }}
        start={<PillCluster><Pill><Button primaryAction onClick={() => openOrganisation(false)}><Icon name="i-building" />Organisation erstellen</Button></Pill>
          <Pill><Button disabled={!session.member_id} aria-label={`Meine Einstellungen: ${account}`} onClick={() => setSettingsOpen(true)}><Icon name="i-person" />{account}</Button></Pill></PillCluster>}
        center={<Tabs items={[{ name: view, label: view === 'erstgespraech' ? "Ausfüllen" : "Coachings", icon: view === 'erstgespraech' ? 'i-edit' : 'i-coaching' }]} activeName={view} onChange={() => {}} />}
        right={<PillCluster><Pill>{view === 'erstgespraech' ? <Button primaryAction onClick={() => { setAttempted(true); gateAdvance(check, () => openOrganisation(true)) }}><Icon name="i-arrow-right" />Weiter</Button>
          : session.variants.some((variant) => variant.key === 'azav') && <Button primaryAction onClick={() => { setStarted(true); setView('erstgespraech') }}><Icon name="i-chat" />Erstgespräch</Button>}</Pill></PillCluster>}
      />
      <FormScroll aria-label={view === 'erstgespraech' ? "Erstgespräch" : "Coachings"}>{view === 'erstgespraech' && <div className="form-column"><FormRenderer
          definition={erstgespraechForm}
          values={values}
          onChange={(path, value) =>
            setValues((current) => {
              const next = structuredClone(current)
              setAt(next, path, value)
              return next
            })
          }
          errors={check.errors}
          attemptedAdvance={attempted}
          field={(field) => ({
            required: erstgespraechManualFields?.has(field.id),
            missing: erstgespraechManualFields?.has(field.id) ? 'mark' : undefined,
          })}
        /></div>}</FormScroll>
    </>}
    {settingsOpen && session.member_id && <Modal label="Meine Einstellungen" onClose={closeSettings} topbar={<TopBar className="bar--responsive bar--navigation modal-head"
      close={{ label: "Schließen" }}
      start={<Pill><span className="btn btn--static"><Icon name="i-person" /><span className="bar-title">{account}</span></span></Pill>}
      center={<Pill><span className="btn btn--static is-active"><Icon name="i-person" /><span className="bar-title">Meine Einstellungen</span></span></Pill>}
      right={<AccountSignOut />} />}>
      <MeineEinstellungen identity={{ userId: session.member_id, ...profileName, email: session.email ?? '', hatPasswort: session.hat_passwort }} onNameChange={changeProfileName} />
    </Modal>}
  </div></div>
}
